/**
 * Profile data formatting utilities
 * Centralizes formatting logic for consistent display across the application
 */

/**
 * Format marital status for display
 * Converts "never_married" to "Never Married", "divorced" to "Divorced", etc.
 */
export const formatMaritalStatus = (value: any): string => {
  if (!value) return 'Not specified';
  
  let status = value;
  
  // Handle object types
  if (typeof value === 'object') {
    status = value.value || value.name || value.status || '';
  }
  
  // Convert to string
  status = String(status);
  
  // Convert "never_married" to "Never Married"
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format annual income for display
 * Converts ranges like "15_20" to "15-20 lakhs"
 * Handles both ranges and single values
 */
export const formatIncome = (value: any): string => {
  if (!value) return 'Not specified';
  
  let income = value;
  
  // Handle object types
  if (typeof value === 'object') {
    income = value.value || value.amount || value.range || '';
  }
  
  // Convert to string
  income = String(income).trim();
  
  if (!income || income === 'Not specified') return 'Not specified';
  
  // If already formatted (contains "lakh" or "crore"), return as is
  if (income.toLowerCase().includes('lakh') || income.toLowerCase().includes('crore')) {
    return income;
  }
  
  // If it's a range like "15_20" or "15-20", format it
  const rangeMatch = income.match(/(\d+)[-_](\d+)/);
  if (rangeMatch) {
    const min = parseInt(rangeMatch[1]);
    const max = parseInt(rangeMatch[2]);
    
    // If both values are >= 100, show in crores
    if (min >= 100 && max >= 100) {
      return `${min / 100}-${max / 100} crores`;
    }
    
    return `${min}-${max} lakhs`;
  }
  
  // If it's just a number, format it
  const numberMatch = income.match(/(\d+)/);
  if (numberMatch) {
    const num = parseInt(numberMatch[1]);
    
    if (num >= 100) {
      return `${num / 100} crores`;
    }
    
    if (num > 0) {
      return `${num} lakhs`;
    }
  }
  
  return income;
};

/**
 * Format height for display
 * Converts cm to feet-inches format
 */
export const formatHeight = (heightInCm: number | string): string => {
  if (!heightInCm) return 'Not specified';
  
  const cm = typeof heightInCm === 'string' ? parseInt(heightInCm) : heightInCm;
  
  if (isNaN(cm) || cm <= 0) return 'Not specified';
  
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  
  return `${feet}'${inches}" (${cm} cm)`;
};

/**
 * Format education level for display
 * Handles both string and object formats
 * Converts "bachelors" to "Bachelors", "phd" to "PhD", etc.
 */
export const formatEducation = (value: any): string => {
  if (!value) return 'Not specified';
  
  let education = value;
  
  if (typeof value === 'object') {
    education = value.level || value.degree || value.name || '';
  }
  
  if (!education) return 'Not specified';
  
  education = String(education).trim();
  
  // Special cases
  const educationMap: Record<string, string> = {
    'high_school': 'High School',
    'diploma': 'Diploma',
    'bachelors': 'Bachelors',
    'masters': 'Masters',
    'phd': 'PhD',
    'other': 'Other'
  };
  
  // Check if it matches a known education level
  const normalized = education.toLowerCase().replace(/\s+/g, '_');
  if (educationMap[normalized]) {
    return educationMap[normalized];
  }
  
  // If not found, capitalize first letter of each word
  return education
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format diet preference for display
 */
export const formatDiet = (value: any): string => {
  if (!value) return 'Not specified';
  
  let diet = value;
  
  if (typeof value === 'object') {
    diet = value.value || value.type || value.preference || '';
  }
  
  diet = String(diet);
  
  // Capitalize first letter
  return diet.charAt(0).toUpperCase() + diet.slice(1).toLowerCase();
};

/**
 * Format smoking/drinking habits for display
 */
export const formatHabit = (value: any): string => {
  if (!value) return 'Not specified';
  
  let habit = value;
  
  if (typeof value === 'object') {
    habit = value.value || value.status || '';
  }
  
  habit = String(habit);
  
  // Handle common values
  const normalized = habit.toLowerCase();
  
  if (normalized === 'no' || normalized === 'never' || normalized === 'non_smoker' || normalized === 'non_drinker') {
    return 'No';
  }
  
  if (normalized === 'yes' || normalized === 'occasionally' || normalized === 'socially') {
    return habit.charAt(0).toUpperCase() + habit.slice(1).toLowerCase();
  }
  
  // Split underscores and capitalize
  return habit
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Extract string value from various data types
 * Handles strings, objects, and arrays
 */
export const extractString = (value: any, defaultValue: string = 'Not specified'): string => {
  if (!value) return defaultValue;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.level) return value.level;
  if (typeof value === 'object' && value.name) return value.name;
  if (typeof value === 'object' && value.value) return value.value;
  if (Array.isArray(value) && value.length > 0) return value[0];
  return defaultValue;
};